import type { MatchSetup } from '@sd/bots';
import type { OutpostKind, TradeGoodId } from '@sd/content';
import { t, type Locale } from '@sd/i18n';
import { leadShip, type FleetState, type ShipState } from '@sd/sim';
import { TICKS_PER_SECOND } from '../game/protocol';

/** Seat colours (docs/design/ui-style.md): fixed per seat, the same on every client. */
export const seatColor = (playerId: string): string => {
  const seat = Number(playerId.replace(/\D/g, ''));
  return seat >= 1 && seat <= 4 ? `var(--seat-${seat})` : 'var(--pirates)';
};

/** A person sees «You», bots go by their character, numbered when two share one. */
export function playerName(locale: Locale, setup: MatchSetup, playerId: string): string {
  const seat = setup.seats.find((s) => s.id === playerId);
  if (!seat) return playerId;
  // A network match names the other people (#139); the viewer's own seat has no name.
  if (seat.control === 'human') return seat.name ?? t(locale, 'ui.lobby.you');
  const name = t(locale, `personality.${seat.control}.name`);
  const twins = setup.seats.filter((s) => s.control === seat.control);
  return twins.length > 1 ? `${name} ${twins.indexOf(seat) + 1}` : name;
}

const formats = new Map<string, Intl.NumberFormat>();

export function num(locale: Locale, value: number, digits = 0): string {
  const key = `${locale}:${digits}`;
  let format = formats.get(key);
  if (!format) {
    format = new Intl.NumberFormat(locale === 'ru' ? 'ru-RU' : 'en-US', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
    formats.set(key, format);
  }
  return format.format(value);
}

/** Game time as m:ss. */
export function clock(ticks: number): string {
  const seconds = Math.max(0, Math.floor(ticks / TICKS_PER_SECOND));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/**
 * An extraction site's name by what it yields (#103, the author's request):
 * «Добыча гелия-3» on a gas giant, «Добыча металлов» on asteroids; a
 * Lagrange point keeps its station.
 */
export function outpostName(
  locale: Locale,
  o: { readonly kind: OutpostKind; readonly good: TradeGoodId },
): string {
  return o.kind === 'lagrangeStation'
    ? t(locale, 'outpost.lagrangeStation.name')
    : t(locale, `outpost.extract.${o.good}`);
}

/** «не хватает: 92 денег, 4 сплавов» (#109): how much, not only that something is short. */
export function shortOf(
  locale: Locale,
  player: {
    readonly cash: number;
    readonly goods: { readonly alloys: number; readonly metals: number };
  },
  cash: number,
  alloys: number,
  metals = 0,
): string | null {
  const list = [
    cash > player.cash
      ? t(locale, 'fleet.short.cash', { n: num(locale, Math.ceil(cash - player.cash)) })
      : '',
    alloys > player.goods.alloys
      ? t(locale, 'fleet.short.alloys', { n: num(locale, Math.ceil(alloys - player.goods.alloys)) })
      : '',
    metals > player.goods.metals
      ? t(locale, 'fleet.short.metals', { n: num(locale, Math.ceil(metals - player.goods.metals)) })
      : '',
  ].filter(Boolean);
  return list.length ? t(locale, 'fleet.short', { list: list.join(', ') }) : null;
}

const VOWELS = /[аеёиоуыэюяaeiouy]/i;
const STAYS = /[ьъй]/i;
const SHY = '­';

/**
 * Soft hyphens between syllables of long words (#152): a narrow tile breaks
 * «Оборони-тельные», not «Оборонительны-е». Browsers do not hyphenate Russian
 * everywhere, so the break points travel with the text.
 */
export function syllables(text: string): string {
  return text.replace(/[\p{L}]{8,}/gu, (word) => {
    let out = word[0]!;
    for (let i = 1; i < word.length; i++) {
      const c = word[i]!;
      const left = i >= 3 && word.length - i >= 3;
      const v = (k: number) => VOWELS.test(word[k] ?? '');
      const consonant = (k: number) => !v(k) && !STAYS.test(word[k] ?? '');
      // A syllable starts at a consonant: «ко-ра», «тер-мо», «ис-сле», «тель-ные».
      const cut =
        left &&
        consonant(i) &&
        VOWELS.test(word.slice(0, i)) &&
        VOWELS.test(word.slice(i)) &&
        ((v(i - 1) && v(i + 1)) || STAYS.test(word[i - 1]!) || (consonant(i - 1) && v(i - 2)));
      out += (cut ? SHY : '') + c;
    }
    return out;
  });
}

/** A ship by its class and number (#154): «Корвет», «Корвет 2». */
export const shipName = (locale: Locale, ship: ShipState): string =>
  `${t(locale, `ship.${ship.cls}.name`)}${(ship.n ?? 1) > 1 ? ` ${ship.n}` : ''}`;

/** A fleet by its heaviest ship, and how many more go with it: «Крейсер +2». */
export function fleetName(locale: Locale, fleet: FleetState): string {
  const name = shipName(locale, leadShip(fleet));
  const more = fleet.ships.length - 1;
  return more > 0 ? t(locale, 'fleet.nameMore', { name, n: more }) : name;
}
